import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { catchError, Observable, of, shareReplay, switchMap } from 'rxjs';
import { Dao } from '../dao';
import { API_ROUTE_ORGANIZATION_SETTINGS_BY_KEY } from './api-router';

@Injectable({
  providedIn: 'root',
})
export class OrganizationSettingsDao extends Dao {
  private settingsCache = new Map<string, Observable<any>>();

  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
  ) {
    super(httpApi, authentificationService);
  }

  getOrganizationSettingByKey(organizationId, key): Observable<any> {
    return this.fetchSetting(organizationId, key).pipe(
      catchError(error => {
        console.error('Error fetching organization setting by key:', error);
        return of({
          value: false,
        });
      }),
    );
  }

  private fetchSetting(organizationId, key): Observable<any> {
    return this.httpApi.get(
      API_ROUTE_ORGANIZATION_SETTINGS_BY_KEY.replace(
        ':organizationId',
        organizationId,
      ).replace(':key', key),
    );
  }

  /**
   * Même appel, mémoïsé par organisation et par clé : le réglage est lu par
   * plusieurs services au chargement (menu, volet de notifications) et la
   * méthode ci-dessus part en réseau à chaque abonnement.
   */
  getOrganizationSettingByKeyCached(organizationId, key): Observable<any> {
    const cacheKey = `${organizationId}:${key}`;
    if (!this.settingsCache.has(cacheKey)) {
      this.settingsCache.set(
        cacheKey,
        this.fetchSetting(organizationId, key).pipe(
          shareReplay(1),
          // Un échec réseau ne reste pas en cache, sinon un aléa passager
          // désactiverait la fonctionnalité pour toute la session.
          catchError(error => {
            this.settingsCache.delete(cacheKey);
            console.error('Error fetching organization setting by key:', error);
            return of({ value: false });
          }),
        ),
      );
    }
    return this.settingsCache.get(cacheKey);
  }

  getCurrentOrganizationSettings(key): Observable<any> {
    return this.getAuthentified().pipe(
      switchMap(user => {
        if (user?.organizationId) {
          return this.getOrganizationSettingByKey(user.organizationId, key);
        } else {
          return of(null);
        }
      }),
    );
  }

  getOrganizationSettingsByKeyCheckIfClient(key): Observable<any> {
    return this.getAuthentified().pipe(
      switchMap(user => {
        if (!!user?.sessionConsultant) {
          return of({
            value: true,
          });
        }
        if (user?.organizationId) {
          return this.getOrganizationSettingByKey(user.organizationId, key);
        } else {
          return of(null);
        }
      }),
    );
  }

  onUserChange(event: any) {
    // Optionnel : le constructeur de Dao émet avant l'initialisation des champs
    // de cette classe.
    this.settingsCache?.clear();
  }
}
